import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  Kcc20UserState,
  Kcc20UserStateApiService,
} from './kcc20-user-state-api.service';

@Injectable({ providedIn: 'root' })
export class Kcc20WatchlistStateService {
  private readonly api = inject(Kcc20UserStateApiService);
  private readonly activeOwner = signal<string | undefined>(undefined);
  private readonly mutatingTokenIds = signal<ReadonlySet<string>>(new Set());
  private loadGeneration = 0;
  private mutationGeneration = 0;
  private mutationQueue: Promise<unknown> | undefined;
  private stateVersion = 0;

  readonly userState = signal<Kcc20UserState | undefined>(undefined);
  readonly loading = signal(false);
  readonly watchlist = computed(() => this.userState()?.watchlist ?? []);

  clear(): void {
    this.activeOwner.set(undefined);
    this.userState.set(undefined);
    this.loading.set(false);
    this.loadGeneration += 1;
    this.mutationGeneration += 1;
    this.resetMutationQueue();
    this.stateVersion += 1;
  }

  setState(state: Kcc20UserState | undefined): void {
    if (!state) {
      this.clear();
      return;
    }
    const normalized = this.normalizeState(state);
    if (this.activeOwner() !== normalized.owner) {
      this.mutationGeneration += 1;
      this.resetMutationQueue();
    }
    this.activeOwner.set(normalized.owner);
    this.userState.set(normalized);
    this.stateVersion += 1;
  }

  async load(owner: string | undefined, requestId?: string): Promise<void> {
    const normalizedOwner = normalizeId(owner);
    if (!normalizedOwner) {
      this.clear();
      return;
    }
    if (this.activeOwner() !== normalizedOwner) {
      this.mutationGeneration += 1;
      this.resetMutationQueue();
    }
    this.activeOwner.set(normalizedOwner);
    if (this.userState()?.owner !== normalizedOwner) {
      this.userState.set(undefined);
      this.stateVersion += 1;
    }
    const generation = ++this.loadGeneration;
    const versionAtStart = this.stateVersion;
    this.loading.set(true);
    try {
      const state = this.normalizeState(
        await firstValueFrom(this.api.getMyState(requestId)),
      );
      this.assertOwnerMatch(normalizedOwner, state);
      if (
        this.activeOwner() === normalizedOwner &&
        this.loadGeneration === generation &&
        this.stateVersion === versionAtStart
      ) {
        this.userState.set(state);
      }
    } finally {
      if (
        this.activeOwner() === normalizedOwner &&
        this.loadGeneration === generation
      ) {
        this.loading.set(false);
      }
    }
  }

  isWatching(tokenId: string | null | undefined): boolean {
    const id = normalizeId(tokenId);
    return !!id && this.watchlist().includes(id);
  }

  isMutating(tokenId: string | null | undefined): boolean {
    const id = normalizeId(tokenId);
    return !!id && this.mutatingTokenIds().has(id);
  }

  async toggle(
    owner: string,
    tokenId: string,
    requestId?: string,
  ): Promise<Kcc20UserState> {
    return this.enqueueMutation((generation) =>
      this.mutate(
        owner,
        tokenId,
        !this.isWatching(tokenId),
        generation,
        requestId,
      ),
    );
  }

  async add(
    owner: string,
    tokenId: string,
    requestId?: string,
  ): Promise<Kcc20UserState> {
    return this.enqueueMutation((generation) =>
      this.mutate(owner, tokenId, true, generation, requestId),
    );
  }

  async remove(
    owner: string,
    tokenId: string,
    requestId?: string,
  ): Promise<Kcc20UserState> {
    return this.enqueueMutation((generation) =>
      this.mutate(owner, tokenId, false, generation, requestId),
    );
  }

  /**
   * Drops the current mutation chain and any stale "mutating" flags on an
   * owner switch, so the new owner's first mutation calls the API right
   * away instead of queueing behind a stalled request from the old owner
   * (the old request's own generation guard in `mutate()` still keeps it
   * from clobbering state once/if it settles).
   */
  private resetMutationQueue(): void {
    this.mutationQueue = undefined;
    this.mutatingTokenIds.set(new Set());
  }

  private enqueueMutation(
    action: (generation: number) => Promise<Kcc20UserState>,
  ): Promise<Kcc20UserState> {
    const generation = this.mutationGeneration;
    const run = () => {
      if (generation !== this.mutationGeneration) {
        return Promise.reject(new Error('watchlist mutation cancelled'));
      }
      return action(generation);
    };
    const next = this.mutationQueue ? this.mutationQueue.then(run, run) : run();
    const tail = next.catch(() => undefined);
    this.mutationQueue = tail;
    void tail.finally(() => {
      if (this.mutationQueue === tail) {
        this.mutationQueue = undefined;
      }
    });
    return next;
  }

  private async mutate(
    owner: string,
    tokenId: string,
    add: boolean,
    generation: number,
    requestId?: string,
  ): Promise<Kcc20UserState> {
    const normalizedOwner = normalizeId(owner);
    const normalizedTokenId = normalizeId(tokenId);
    if (!normalizedOwner || !normalizedTokenId) {
      throw new Error('watchlist owner and token id are required');
    }

    const previous = this.userState();
    const baseline =
      previous?.owner === normalizedOwner
        ? previous
        : emptyState(normalizedOwner);
    this.activeOwner.set(normalizedOwner);
    this.setMutating(normalizedTokenId, true);
    this.userState.set(this.withToken(baseline, normalizedTokenId, add));
    this.stateVersion += 1;

    try {
      const updated = this.normalizeState(
        await firstValueFrom(
          add
            ? this.api.addMyWatchlist(normalizedTokenId, requestId)
            : this.api.removeMyWatchlist(normalizedTokenId, requestId),
        ),
      );
      this.assertOwnerMatch(normalizedOwner, updated);
      if (this.isCurrentMutation(normalizedOwner, generation)) {
        this.userState.set(updated);
        this.stateVersion += 1;
      }
      return updated;
    } catch (error) {
      if (this.isCurrentMutation(normalizedOwner, generation)) {
        this.userState.set(
          previous?.owner === normalizedOwner ? previous : undefined,
        );
        this.stateVersion += 1;
      }
      throw error;
    } finally {
      // A stale request (superseded by an owner switch) must not clear the
      // mutating flag for a token id a newer mutation may now be using —
      // that flag belongs to the current generation's request until it
      // settles.
      if (this.isCurrentMutation(normalizedOwner, generation)) {
        this.setMutating(normalizedTokenId, false);
      }
    }
  }

  private isCurrentMutation(owner: string, generation: number): boolean {
    return (
      this.activeOwner() === owner && this.mutationGeneration === generation
    );
  }

  private withToken(
    state: Kcc20UserState,
    tokenId: string,
    add: boolean,
  ): Kcc20UserState {
    const next = new Set(state.watchlist.map((id) => id.toLowerCase()));
    if (add) {
      next.add(tokenId);
    } else {
      next.delete(tokenId);
    }
    return this.normalizeState({ ...state, watchlist: Array.from(next) });
  }

  private normalizeState(state: Kcc20UserState): Kcc20UserState {
    return {
      ...state,
      owner: state.owner.toLowerCase(),
      watchlist: normalizeList(state.watchlist),
      savedMarkets: normalizeList(state.savedMarkets),
    };
  }

  private setMutating(tokenId: string, mutating: boolean): void {
    const next = new Set(this.mutatingTokenIds());
    if (mutating) {
      next.add(tokenId);
    } else {
      next.delete(tokenId);
    }
    this.mutatingTokenIds.set(next);
  }

  private assertOwnerMatch(expectedOwner: string, state: Kcc20UserState): void {
    if (state.owner !== expectedOwner) {
      throw new Error('watchlist owner mismatch');
    }
  }
}

function normalizeId(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim().toLowerCase();
  return trimmed || undefined;
}

function normalizeList(values: string[] | undefined): string[] {
  return Array.from(
    new Set(
      (values ?? [])
        .map((value) => normalizeId(value))
        .filter((value): value is string => !!value),
    ),
  );
}

function emptyState(owner: string): Kcc20UserState {
  return {
    owner,
    watchlist: [],
    savedMarkets: [],
    preferences: {},
    drafts: [],
    notifications: {
      fills: true,
      indexedTxs: true,
      watchedMarkets: true,
      stalePendingTxs: true,
    },
  };
}
